/**
 * EditableMultiselectCell - A self-contained inline-editable multiselect cell for tables
 *
 * Mirrors EditableDropdownCell pattern:
 * - Click-to-edit: container onClick calls onCellEdit(context) so table state is set on first click
 * - Read-only: shows as many tags as fit in the column width, then "+N"
 * - When editing: single MultiselectAutocomplete for both API-backed and static options
 * - Saves on Enter key press or click-away
 * - Shows selected values as deletable Chip tags in edit mode
 * - Sorts options with selected first, then alphabetically
 *
 * Used for Categories, custom field multiselects, and other multiselect columns.
 */

import { useState, useCallback, useRef, useEffect, useMemo, memo } from 'react';
import type { CellContext } from '@tanstack/react-table';
import { ClickAwayListener } from '@mui/material';
import type { AutocompleteRenderValueGetItemProps } from '@mui/material';
import { CaretDown, PencilSimple } from '@phosphor-icons/react';
import { t } from '@lingui/macro';
import { toast } from 'react-toastify';
import { Tooltip } from 'components/tooltip';
import { AccessControl, useFeatureFlag } from 'features/accessControl';
import type { ComboBoxApi, ComboBoxOption } from 'components/inputs/types';
import { useEscapeToClose, useFocusOnEdit, useRestoreFocusOnStay } from '../../../hooks';
import { prepareInlineCellHandoff, readActiveEditableSlot } from './commitActiveEditor';
import {
  useEditableContextOptional,
  useShouldSkipInlineCommit,
} from '../../OptimizedTableProvider';
import { MultiselectEditorContainer } from '../../tableStyles';
import { TableTooltip } from '../../TableTooltip';
import { DotsMenuButton } from '../../../components/DotsMenuButton';
import {
  ReadWrapper,
  ReadInner,
  PlaceholderText,
  DotsMenuButtonWrapper,
  TooltipAnchor,
} from './EditableDropdownCell';
import { TagsWithOverflow } from 'components/tags/TagsWithOverflow';
import {
  MULTISELECT_INPUT_SX,
  MULTISELECT_INPUT_ERROR_SX,
  MULTISELECT_PAPER_PROPS,
  MULTISELECT_POPPER_PROPS,
} from './multiselectInlineEditStyles';
import { renderMultiselectTags } from '../multiselectInlineEditUtils';
import { MultiselectAutocomplete } from '../multiselect';

export type EditableMultiselectCellProps<T extends { id: number | string }> = {
  context: CellContext<T, unknown>;
  /** Currently selected values for display and edit */
  value: ComboBoxOption[];
  /** Callback when selection changes - receives row and partial update with new value array. */
  onUpdate: (row: T, update: Partial<T>) => void;
  /** Tooltip text (e.g. full list of labels) */
  tooltipTitle?: string;
  /** Optional when used inside OptimizedTableProvider (read from context) */
  editableRowIndex?: number | null;
  editableColumnId?: string | null;
  onCellEdit?: (context: CellContext<T, unknown>) => void;
  onClickAway?: () => void;
  placeholder?: string;
  /** API config for options (API-backed path); when set, MultiselectAutocomplete fetches options from API */
  api?: ComboBoxApi | null;
  /** Static options (used when api is not set); MultiselectAutocomplete uses these directly */
  options?: ComboBoxOption[];
  /** Column id used for the update payload key and Autocomplete id */
  columnId: string;
  /** Whether this field is required (hides "Set empty" option in dropdown) */
  required?: boolean;
  /**
   * Inline validation message shown when the user empties a required field
   * (e.g. a mandatory-for-export custom field). When omitted, the legacy
   * toast-based feedback is used for a blocked clear (dl-60606).
   */
  requiredMessage?: string;
  /** Column label for the inline error when clearing a required field */
  columnLabel?: string;
  /** Whether to show error styling on the cell */
  error?: boolean;
  /** Whether the cell is disabled (non-editable, muted styling) */
  disabled?: boolean;
  /**
   * Optional validation run before committing a CHANGED selection (click-away / Enter /
   * handoff). Returns an error message to block the commit — a toast is shown and the cell
   * stays in edit mode so the user can fix it (or press Esc to cancel, reverting to the
   * initial value) — or undefined to allow it. An UNCHANGED selection is never validated:
   * exiting a cell whose stored value is already invalid is always allowed. Used by
   * custom-field multiselects to refuse a selection that still contains a deactivated
   * allowed value.
   */
  validateSelection?: (value: ComboBoxOption[]) => string | undefined;
};

const EditableMultiselectCellInner = <T extends { id: number | string }>({
  context,
  value,
  onUpdate,
  tooltipTitle,
  editableRowIndex: editableRowIndexProp,
  editableColumnId: editableColumnIdProp,
  onCellEdit: onCellEditProp,
  onClickAway: onClickAwayProp,
  placeholder = '',
  api,
  options = [],
  columnId,
  required,
  requiredMessage,
  columnLabel,
  error: errorProp,
  disabled: disabledProp,
  validateSelection,
}: EditableMultiselectCellProps<T>) => {
  const editableCtx = useEditableContextOptional();
  const editableState = editableCtx?.editableState ?? null;
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);
  // The "Set empty" dropdown footer is gated by its own feature flag (shared with
  // bulk edit's set-empty footer), independent of the inline-edit flag.
  const isClearFieldEnabled = useFeatureFlag('BulkEditClearField');

  const editableRowIndex = editableRowIndexProp ?? editableState?.rowIndex ?? null;
  const editableColumnId = editableColumnIdProp ?? editableState?.columnId ?? null;
  const isEditing =
    context.row.index === editableRowIndex && context.column.id === editableColumnId;

  const onCellEdit =
    onCellEditProp ??
    (() => {
      setEditableState({ rowIndex: context.row.index, columnId: context.column.id });
    });
  const onClickAway =
    onClickAwayProp ??
    (() => {
      setEditableState({ rowIndex: null, columnId: null });
    });

  // Track local selection state for API mode (to support save on click-away)
  const [localValue, setLocalValue] = useState<ComboBoxOption[]>(value);
  // Inline validation error shown when a required field is emptied (dl-60606).
  const [localError, setLocalError] = useState<string | undefined>(undefined);
  const isEditingRef = useRef(false);
  const editorRef = useRef<HTMLDivElement>(null);
  // The option the user has navigated to with the arrow keys, or null when none
  // is highlighted. Drives the Enter behaviour: add the highlighted option (keep
  // open) vs. commit the current selection and close.
  const highlightedRef = useRef<ComboBoxOption | null>(null);
  // Always holds the latest `value` so the edit-entry effect can seed from it without
  // taking `value` as a reactive dependency (re-seeding mid-edit would clobber the
  // in-progress selection mid-edit).
  const valueRef = useRef(value);
  valueRef.current = value;
  // Snapshot of the persisted selection taken when the cell ENTERS edit mode. The
  // change check compares against this (not the live `value` prop) so an optimistic
  // rollback does not make an unchanged selection look different.
  const savedValueRef = useRef<ComboBoxOption[]>(value);
  // True while THIS cell owns the shared edit slot (mirrors `isEditing`, updated each render).
  // The handoff-commit effect reads it; `selfExitRef` marks the isEditing transition our own
  // save/escape causes so the handoff effect doesn't re-commit it.
  const isActiveRef = useRef(false);
  const selfExitRef = useRef(false);
  const prevIsEditingRef = useRef(isEditing);
  // Sync local value when prop changes (but not during editing)
  useEffect(() => {
    if (!isEditingRef.current) {
      setLocalValue(value);
    }
  }, [value]);

  // Mirror slot ownership every render so the handoff-commit effect reads the live value.
  useEffect(() => {
    isActiveRef.current = isEditing;
  });

  // Initialise edit state when the cell ENTERS edit mode. Keyed on `isEditing` only:
  // seeding reads the latest value from `valueRef`, so a mid-edit `value` change — e.g.
  // usePut's onError rollback after a failed save — does not wipe the attempted selection
  // or the inline error (DL-60176). External value changes while not editing are handled
  // by the effect above.
  useEffect(() => {
    if (isEditing) {
      isEditingRef.current = true;
      setLocalValue(valueRef.current);
      setLocalError(undefined);
      // Snapshot the persisted selection so the change check is stable for this session.
      savedValueRef.current = valueRef.current;
      // No option is highlighted on open, so Enter commits & closes until the
      // user arrow-navigates to a specific option.
      highlightedRef.current = null;
    } else {
      isEditingRef.current = false;
    }
  }, [isEditing]);

  // Skip the commit while a navigation is being confirmed (the guard raises the flag in a
  // capture-phase pointerdown before the nav click, and on dialog open). Without this the
  // leave-dialog's focus-steal fires the ClickAwayListener → handleSave → the edit is sent and the
  // cell closes before the user answers; with it "Leave" / Esc truly discard (DL-60097).
  const shouldSkipCommit = useShouldSkipInlineCommit();

  // Check if the selection has changed from the snapshot taken at edit-entry (not the
  // live `value` prop, which can roll back on a failed save — DL-60176).
  const hasSelectionChanged = useCallback(() => {
    const savedIds = new Set(savedValueRef.current.map(v => String(v.id)));
    const localIds = new Set(localValue.map(v => String(v.id)));
    return savedIds.size !== localIds.size || [...localIds].some(id => !savedIds.has(id));
  }, [localValue]);

  // Save the current selection (Enter key / click-away) and close.
  const handleSave = useCallback(() => {
    // A leave-confirmation dialog is open (its focus-steal fired click-away) — don't commit or
    // exit; the navigation guard owns this interaction and keeps the cell editing (DL-60097).
    if (shouldSkipCommit()) return;

    // Nothing changed — just close the editor. An unchanged selection is never
    // validated, so exiting a cell whose stored value is already invalid is allowed.
    if (!hasSelectionChanged()) {
      isEditingRef.current = false;
      selfExitRef.current = true;
      onClickAway();
      return;
    }

    // A required field cannot be emptied: show the inline message and stay in edit
    // mode instead of sending an empty selection, matching Text/Date CFs (dl-60606).
    // Only blocks when an inline message is supplied; without one, the empty save
    // proceeds and any server-side rejection surfaces via the global error toast.
    if (required && requiredMessage && localValue.length === 0) {
      setLocalError(requiredMessage);
      return;
    }

    // The selection changed: refuse to commit one that is still invalid (e.g. it
    // retains a deactivated allowed value). Toast and keep the cell open so the user
    // can fix it — or press Esc to cancel, reverting to the initial value.
    const validationError = validateSelection?.(localValue);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const updateValue = localValue.map(v => ({ id: v.id, name: v.label ?? '' }));
    onUpdate(context.row.original, { [columnId]: updateValue } as Partial<T>);
    isEditingRef.current = false;
    selfExitRef.current = true;
    onClickAway();
  }, [
    shouldSkipCommit,
    hasSelectionChanged,
    validateSelection,
    localValue,
    onUpdate,
    context.row.original,
    columnId,
    onClickAway,
    required,
    requiredMessage,
  ]);

  // "Set empty" footer: empty the selection but stay in edit mode so the user can
  // add new values. The cleared selection is persisted on the next click-away/Enter.
  //
  // Emptying the value makes MUI rebuild the search <input> element once (the focused node
  // is removed from the DOM — confirmed via focusout with relatedTarget=null and no .blur()
  // call), and nothing refocuses the new input, so the cell silently lost focus (DL-60135).
  // Re-focus the (new) input across the next few frames so we land it after MUI's rebuild
  // settles, then stop — bounded, so it can't thrash. It only acts while still editing and
  // when focus has actually drifted off the input, so it won't fight a real click-away.
  const handleSetEmpty = useCallback(() => {
    setLocalValue([]);
    let framesLeft = 3;
    const restoreFocus = (): void => {
      if (!isEditingRef.current) return;
      const input = editorRef.current?.querySelector<HTMLElement>('input');
      if (input && document.activeElement !== input) input.focus();
      if (--framesLeft > 0) requestAnimationFrame(restoreFocus);
    };
    requestAnimationFrame(restoreFocus);
  }, []);

  // Activate on mousedown rather than click: when another cell is mid-edit, its
  // blur-save (fired during this mousedown's focus change) triggers an optimistic
  // update that rebuilds the columns and remounts every cell between mousedown and
  // mouseup, so the click never fires here (DL-60264). The microtask defer lets the
  // outgoing cell's blur/click-away reset run first so it doesn't clobber us.
  const handleActivate = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      if (!isEditing && !disabledProp && context.row.original.id !== -1) {
        prepareInlineCellHandoff(
          () => readActiveEditableSlot(editableCtx),
          () => setEditableState({ rowIndex: null, columnId: null }),
          context.row.index,
          context.column.id
        );
        Promise.resolve().then(() => onCellEdit(context));
      }
    },
    [isEditing, disabledProp, context, onCellEdit, editableCtx, setEditableState]
  );

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      // The options popup (and its "Set empty" footer) is portaled outside the editor's DOM,
      // so a click there reads as a click-away and would wrongly commit + close the cell —
      // e.g. "Set empty" cleared the value but also exited edit mode (DL-60176). Ignore clicks
      // inside the autocomplete popper; a real outside click still saves.
      const target = event.target as Element | null;
      if (target?.closest?.('.MuiAutocomplete-popper')) return;
      handleSave();
    },
    [handleSave]
  );

  const handleEscape = useCallback(() => {
    setLocalValue(value);
    setLocalError(undefined);
    isEditingRef.current = false;
    selfExitRef.current = true;
    onClickAway();
  }, [value, onClickAway]);

  useEscapeToClose(isEditing, handleEscape);

  // Commit a pending change when ANOTHER cell takes over the shared edit slot (handoff). The
  // ClickAwayListener can't cover this: it unmounts in the same click that moves the slot, so
  // onClickAway never fires (so the multi-select silently lost edits when the next-clicked cell
  // used the shared edit state — every cell on Items, and Text/Numeric/Multiselect on Styles —
  // DL-60176). We detect the slot loss here; `selfExitRef` distinguishes our own save/escape.
  useEffect(() => {
    const wasEditing = prevIsEditingRef.current;
    prevIsEditingRef.current = isEditing;
    if (!wasEditing || isEditing) return; // only act on a true -> false transition
    if (selfExitRef.current) {
      selfExitRef.current = false; // our own save / escape already handled the exit
      return;
    }
    isEditingRef.current = false;
    // External takeover: commit the pending selection (if changed). Errors surface via the
    // shared error toast (the cell no longer owns the slot to show anything inline).
    if (shouldSkipCommit() || !hasSelectionChanged()) return;
    // Don't persist an empty selection for a required field on handoff — the clear is
    // invalid; the value is simply left unchanged (dl-60606).
    if (required && requiredMessage && localValue.length === 0) return;
    // Never commit a still-invalid selection on handoff — mirrors the click-away/Enter guard.
    if (validateSelection?.(localValue)) return;
    const updateValue = localValue.map(v => ({ id: v.id, name: v.label ?? '' }));
    onUpdate(context.row.original, { [columnId]: updateValue } as Partial<T>);
  }, [
    isEditing,
    shouldSkipCommit,
    hasSelectionChanged,
    validateSelection,
    localValue,
    onUpdate,
    context.row.original,
    columnId,
    required,
    requiredMessage,
  ]);

  // Enter is context-sensitive for the multi-select editor:
  //  - An option is highlighted (arrow keys) → let MUI add it and keep the
  //    dropdown open (disableCloseOnSelect). We do nothing here so the event
  //    reaches MUI; the editor's bubble-phase onKeyDown stops it leaking to the
  //    table afterwards.
  //  - Nothing highlighted → commit the current selection and close.
  // Capture phase on document (mirrors useEscapeToClose) so the decision runs
  // before MUI's own keydown handler.
  useEffect(() => {
    if (!isEditing) return;
    const onKeyDownCapture = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (highlightedRef.current == null) {
        e.preventDefault();
        e.stopPropagation();
        handleSave();
      }
    };
    document.addEventListener('keydown', onKeyDownCapture, true);
    return () => document.removeEventListener('keydown', onKeyDownCapture, true);
  }, [isEditing, handleSave]);

  // Focus the search input as soon as the cell enters edit mode so the user can
  // type to filter immediately (RAF: the Autocomplete input mounts on open).
  useFocusOnEdit(editorRef, isEditing, 'input', true);

  // After the leave-confirmation dialog closes via "Stay", MUI returns focus to the nav link
  // (not our still-open popper). Pull it back so the user can keep navigating options without
  // re-clicking the cell (DL-60097).
  useRestoreFocusOnStay(editorRef, isEditing, 'input');

  // When opening a cell that already has selections, scroll the (nowrap) tag row
  // to the end so the input and its caret — which sit after the tags — are visible.
  useEffect(() => {
    if (!isEditing || value.length === 0) return;
    const id = requestAnimationFrame(() => {
      const inputRoot = editorRef.current?.querySelector<HTMLElement>('.MuiInputBase-root');
      if (inputRoot) inputRoot.scrollLeft = inputRoot.scrollWidth;
    });
    return () => cancelAnimationFrame(id);
  }, [isEditing, value.length]);

  const colWidth = context.column.getSize() - 42;
  // Read-mode inner content — mirrors EditableDropdownCell (underscore placeholder
  // or tags), so the multiselect cell shares the single-select cell's look.
  const readInner = useMemo(
    () => (
      <ReadInner width={colWidth}>
        {value.length === 0 ? (
          <PlaceholderText>_</PlaceholderText>
        ) : (
          <TagsWithOverflow values={value} columnWidth={colWidth} />
        )}
      </ReadInner>
    ),
    [value, colWidth]
  );

  // Memoize autocomplete onChange handler (handles "Clear" option - only clears selection; exit is on click-away or Enter)
  const handleAutocompleteChange = useCallback(
    (_: unknown, newValue: ComboBoxOption[] | null) => {
      // A selection consumes the current highlight, so the next plain Enter
      // commits & closes rather than re-adding the same option.
      highlightedRef.current = null;
      const hasClear = newValue?.some(v => String(v?.id) === '__clear__');
      if (hasClear) {
        if (required) {
          // Defence-in-depth: the "Set empty" footer is hidden for required fields
          // (showClearOption is gated on !required), so this is rarely reachable. Show the
          // inline mandatory message when one is supplied (dl-60606), otherwise fall back to
          // the legacy toast; either way keep the current selection.
          if (requiredMessage) {
            setLocalError(requiredMessage);
          } else {
            toast.error(t`${columnLabel || 'This field'} is required and cannot be cleared`);
          }
          return;
        }
        setLocalValue([]);
        return;
      }
      // A real selection resolves any pending required-empty error.
      setLocalError(undefined);
      setLocalValue(newValue ?? []);
    },
    [required, requiredMessage, columnLabel]
  );

  // Track the keyboard-highlighted option. Only keyboard navigation counts as
  // "a specific value chosen"; mouse hover does not change the Enter behaviour.
  const handleHighlightChange = useCallback(
    (_event: React.SyntheticEvent, option: ComboBoxOption | null, reason: string) => {
      if (reason === 'keyboard') highlightedRef.current = option ?? null;
    },
    []
  );

  // Memoize autocomplete onClose handler
  const handleAutocompleteClose = useCallback(
    (_event: React.SyntheticEvent, reason: string) => {
      // Only close on escape - click-away is handled by ClickAwayListener
      if (reason === 'escape') {
        handleEscape();
      }
    },
    [handleEscape]
  );

  const hasError = !!errorProp;

  const editorContent = (
    <MultiselectEditorContainer
      ref={editorRef}
      // When an option is highlighted, MUI (which runs first) adds it on Enter;
      // stop the keystroke here so it doesn't bubble to the table. The commit &
      // close case is already handled (and stopped) by the capture-phase listener.
      onKeyDown={e => {
        if (e.key === 'Enter') e.stopPropagation();
      }}
    >
      <MultiselectAutocomplete
        id={`${columnId}-inline`}
        onHighlightChange={handleHighlightChange}
        placeholder={placeholder}
        api={api ?? undefined}
        options={options}
        // Fetch options as soon as the dropdown opens (no typed search required),
        // matching the single-select EditableDropdownCell/ComboBox behaviour.
        allowEmptySearchFetch
        variant="standard"
        labelSeparate
        multiple
        forcePopupIcon
        popupIcon={<CaretDown size={20} />}
        value={localValue}
        sortSelectedFirst
        selectedValues={localValue}
        renderValue={(tagValue, getItemProps: AutocompleteRenderValueGetItemProps<true>) =>
          renderMultiselectTags([...tagValue], getItemProps, colWidth)
        }
        open={isEditing}
        onClose={handleAutocompleteClose}
        onChange={handleAutocompleteChange}
        disableCloseOnSelect
        disableClearable
        clearOption={{ id: '__clear__', label: t`Set empty` }}
        // Gate the footer on the flag + required only (NOT the live selection), so it stays
        // mounted for the whole edit session. Tying it to localValue.length made clicking
        // "Set empty" toggle the footer off and remount the dropdown mid-session (DL-60135).
        showClearOption={isClearFieldEnabled && !required}
        onSetEmpty={handleSetEmpty}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          if (option.id === '__clear__' || val.id === '__clear__') return false;
          return String(option.id) === String(val.id);
        }}
        getOptionLabel={option => {
          if (typeof option === 'string') return option;
          return option?.label ?? '';
        }}
        sx={hasError ? MULTISELECT_INPUT_ERROR_SX : MULTISELECT_INPUT_SX}
        slotProps={{
          ...MULTISELECT_PAPER_PROPS,
          ...MULTISELECT_POPPER_PROPS,
        }}
      />
    </MultiselectEditorContainer>
  );

  const isInteractive = !disabledProp && !isEditing;
  const isAnyCellEditing = editableRowIndex != null;
  const showDotsMenu = isInteractive && context.row.original.id !== -1;

  // 3-dot "Edit" affordance, mirroring EditableDropdownCell. The activation is
  // deferred one microtask so the previously-edited cell's ClickAwayListener can
  // reset editableState first (otherwise it would clobber this cell's activation).
  const menuOptions = useMemo(
    () => [
      {
        label: t`Edit`,
        icon: <PencilSimple data-testid="pencil_simple_icon" size={16} />,
        onClick: () => {
          Promise.resolve().then(() => onCellEdit(context));
        },
      },
    ],
    [onCellEdit, context]
  );

  // Read-only fallback for users without write access: the read view with no
  // hover affordance, click-to-edit, or dots menu.
  const readFallback = (
    <ReadWrapper $editable={false} $disabled={!!disabledProp} $hasError={!!errorProp}>
      {readInner}
    </ReadWrapper>
  );

  if (isEditing) {
    return (
      <AccessControl write fallback={readFallback}>
        {/* Tooltip anchors on the wrapper box (not the editor) so it never clobbers
            editorRef — ClickAwayListener stays the element that forks editorRef onto
            the editor, keeping focus-restore + click-away intact (dl-60606). */}
        <Tooltip
          title={localError ?? ''}
          open={!!localError}
          placement="bottom-start"
          arrow
          error
          followCursor={false}
        >
          <TooltipAnchor>
            <ClickAwayListener onClickAway={handleClickAway}>{editorContent}</ClickAwayListener>
          </TooltipAnchor>
        </Tooltip>
      </AccessControl>
    );
  }

  return (
    <AccessControl write fallback={readFallback}>
      <ReadWrapper
        $editable={isInteractive}
        $disabled={!!disabledProp}
        $hasError={!!errorProp}
        onMouseDown={handleActivate}
      >
        {isAnyCellEditing ? (
          readInner
        ) : (
          <TableTooltip title={tooltipTitle ?? ''}>{readInner}</TableTooltip>
        )}
      </ReadWrapper>
      {showDotsMenu && (
        <DotsMenuButtonWrapper>
          <DotsMenuButton menuOptions={menuOptions} />
        </DotsMenuButtonWrapper>
      )}
    </AccessControl>
  );
};

// Wrap with memo for performance optimization
export const EditableMultiselectCell = memo(EditableMultiselectCellInner) as <
  T extends { id: number | string },
>(
  props: EditableMultiselectCellProps<T>
) => JSX.Element;
